import { Box, Button, Divider, Typography } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { smallButtonProps } from "../../utils/styles";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import axios from "axios";
import { getValidToken } from "../../utils/authUtils";
import { handleLogout } from "../../utils/utils";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { useToast } from "../../components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { Loading } from "../../components/Loading";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const ActivationDialog = ({
  price,
  onClose,
}: {
  price: number;
  onClose: () => void;
}) => {
  const { showToast } = useToast();
  const { planStatus, invalidatePlanStatus } = usePlanStatus();
  const queryClient = useQueryClient();
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [razorpayLoadError, setRazorpayLoadError] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const keyFeatures = useMemo(
    () => [
      "Booking Management",
      "Daily Trip Start & End Checklist",
      "Cash Book",
      "Sales Tracking & Collection",
      "Documentation & Alerts",
      "Smart Reminders",
      "Expense Tracking",
      "Maintenance & Service Tracker",
      "Customer Review & Feedback",
      "Trip Tracking & RTO Fines",
      "Driver Blacklisting",
      "TAX & GST Calculation",
      "Monthly Business Analytics",
      "Profit & Loss Overview",
    ],
    []
  );

  const INITIAL_FEATURES_COUNT = 6;
  const featuresToShow = showAllFeatures
    ? keyFeatures
    : keyFeatures.slice(0, INITIAL_FEATURES_COUNT);

  const resetStates = () => {
    setIsSubmitting(false);
    setIsProcessing(false);
    setLoadingStep("");
  };

  const createPaymentOrderWithTimeout = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const token = await getValidToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}payments/orders/activation`,
        {
          amount: price,
          orderType: "ACCOUNT_ACTIVATION",
          description: "Account activation fee for Gaadi Kharcha",
          companyId: planStatus?.companyId,
          dueDate: new Date().toISOString(),
          referenceId: planStatus?.companyId,
          referenceType: "COMPANY",
          isRecurring: false,
          recurringFrequency: null,
          additionalData: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          timeout: 25000,
        }
      );

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const createPaymentOrder = async () => {
    if (isSubmitting || isProcessing) {
      return;
    }

    try {
      setIsSubmitting(true);
      setIsProcessing(true);
      setLoadingStep("Initializing payment...");

      if (!razorpayLoaded) {
        setLoadingStep("Loading payment gateway...");
        let attempts = 0;
        while (!razorpayLoaded && attempts < 20) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          attempts++;
        }

        if (!razorpayLoaded) {
          throw new Error(
            "Payment gateway failed to load. Please refresh the page and try again."
          );
        }
      }

      setLoadingStep("Creating payment order...");
      const response = await createPaymentOrderWithTimeout();

      if (!response.data?.isSuccess) {
        throw new Error(
          response.data?.message || "Failed to create payment order"
        );
      }

      const { order, paymentSession } = response.data.data;

      if (!order || !paymentSession?.orderId) {
        throw new Error("Invalid payment order data received");
      }

      setLoadingStep("Opening payment gateway...");

      const razorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: "INR",
        order_id: paymentSession.orderId,
        name: order.name || "Gaadi Kharcha",
        description: order.description || "Account activation fee",
        image: order.image,
        modal: {
          escape: false,
          ondismiss: () => {
            setLoadingStep("");
            setIsSubmitting(false);
            setIsProcessing(false);
            showToast("Payment was cancelled", "warning");
          },
        },
        handler: async (paymentResponse: any) => {
          await handlePaymentSuccess(paymentResponse);
        },
        prefill: {
          name: order.customerName || "",
          email: order.customerEmail || "",
          phone: order.customerPhone || "",
        },
        theme: {
          color: "#1976d2",
        },
      };

      const razorpay = new window.Razorpay(razorpayOptions);

      razorpay.on("payment.failed", (response: any) => {
        console.error("Razorpay payment failed:", response.error);
        resetStates();
        showToast(
          response.error?.description || "Payment failed. Please try again.",
          "error"
        );
      });

      razorpay.open();
    } catch (error: any) {
      console.error("Error in payment flow:", error);
      resetStates();

      let errorMessage = "An unexpected error occurred. Please try again.";

      if (error.name === "AbortError") {
        errorMessage =
          "Request timed out. Please check your connection and try again.";
      } else if (error.response?.status === 401) {
        errorMessage = "Session expired. Please login again.";
        handleLogout(queryClient);
        return;
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again in a few moments.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    }
  };

  const handlePaymentSuccess = async (paymentResponse: any) => {
    try {
      if (!paymentResponse || typeof paymentResponse !== "object") {
        throw new Error("Invalid payment response format");
      }

      if (
        !paymentResponse.razorpay_payment_id ||
        !paymentResponse.razorpay_order_id ||
        !paymentResponse.razorpay_signature
      ) {
        throw new Error("Missing required payment details in response");
      }

      if (!paymentResponse.razorpay_payment_id.startsWith("pay_")) {
        throw new Error("Invalid payment ID format");
      }

      if (!paymentResponse.razorpay_order_id.startsWith("order_")) {
        throw new Error("Invalid order ID format");
      }

      await verifyPayment(paymentResponse);
    } catch (error: any) {
      console.error("Error handling payment success:", error);
      resetStates();
      const errorMessage =
        error.message ||
        "Payment completed but verification failed. Please contact support with your payment details.";
      showToast(errorMessage, "error");
    }
  };

  const verifyPaymentWithTimeout = async (paymentResponse: any) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const token = await getValidToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}payments/razorpay/verify`,
        {
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          timeout: 25000,
        }
      );

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const verifyPayment = async (paymentResponse: any) => {
    try {
      setLoadingStep("Verifying payment...");

      const response = await verifyPaymentWithTimeout(paymentResponse);

      if (!response.data?.isSuccess) {
        throw new Error(
          response.data?.message || "Payment verification failed"
        );
      }

      setLoadingStep("Payment verified successfully");
      await updateCompanyPlanStatus();

      showToast(
        "🎉 Account activated successfully! Welcome to Gaadi Kharcha!",
        "success"
      );

      setTimeout(() => {
        resetStates();
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Error verifying payment:", error);
      resetStates();

      let errorMessage =
        "Payment verification failed. Please contact support with your payment details.";

      if (error.name === "AbortError") {
        errorMessage =
          "Verification timed out. Your payment may still be processed. Please contact support.";
      } else if (error.response?.status === 401) {
        errorMessage =
          "Session expired during verification. Please contact support with your payment details.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    }
  };

  const updateCompanyPlanStatusWithTimeout = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}company/${planStatus?.companyId}`;

      const planExpiryDate = new Date();
      planExpiryDate.setDate(planExpiryDate.getDate() + 30);

      const response = await axios.put(
        url,
        {
          status: "ACTIVE",
          verificationStatus: "VERIFIED",
          planType: "MONTHLY",
          planExpiry: planExpiryDate.toISOString(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          timeout: 15000,
        }
      );

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const updateCompanyPlanStatus = async () => {
    try {
      setLoadingStep("Activating your account...");

      const response = await updateCompanyPlanStatusWithTimeout();

      if (!response.data?.isSuccess) {
        throw new Error(response.data?.message || "Failed to activate account");
      }

      // Invalidate plan status to refresh data
      invalidatePlanStatus();
      window.location.reload();
      setLoadingStep("Account activated successfully!");
    } catch (error: any) {
      console.error("Error updating company plan status:", error);

      let errorMessage =
        "Failed to activate account. Your payment was successful. Please contact support.";

      if (error.name === "AbortError") {
        errorMessage =
          "Account activation timed out. Your payment was successful. Please contact support.";
      } else if (error.response?.status === 401) {
        errorMessage =
          "Session expired during activation. Your payment was successful. Please contact support.";
      }

      showToast(errorMessage, "error");
      throw error;
    }
  };

  const retryRazorpaySDK = () => {
    setRazorpayLoadError(false);
    setRazorpayLoaded(false);

    // Remove existing script if any
    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existingScript) {
      existingScript.remove();
    }

    loadRazorpaySDK();
  };

  const loadRazorpaySDK = () => {
    // Check if Razorpay SDK is already loaded
    if (typeof window.Razorpay !== "undefined") {
      setRazorpayLoaded(true);
      setRazorpayLoadError(false);
      return;
    }

    // Load Razorpay SDK from CDN
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.crossOrigin = "anonymous";

    // Set a timeout for loading
    const loadTimeout = setTimeout(() => {
      if (!razorpayLoaded) {
        console.error("Razorpay SDK loading timed out");
        setRazorpayLoadError(true);
        setRazorpayLoaded(false);
      }
    }, 15000);

    script.onload = () => {
      clearTimeout(loadTimeout);
      // Double check if Razorpay is actually available
      if (typeof window.Razorpay !== "undefined") {
        setRazorpayLoaded(true);
        setRazorpayLoadError(false);
      } else {
        console.error("Razorpay SDK loaded but not available");
        setRazorpayLoadError(true);
        setRazorpayLoaded(false);
      }
    };

    script.onerror = () => {
      clearTimeout(loadTimeout);
      console.error("Failed to load Razorpay SDK");
      setRazorpayLoadError(true);
      setRazorpayLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      clearTimeout(loadTimeout);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  };

  // Load Razorpay SDK with improved error handling
  useEffect(() => {
    const cleanup = loadRazorpaySDK();

    // Cleanup on unmount
    return () => {
      if (cleanup) cleanup();
      resetStates();
    };
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        zIndex: 1200,
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "center",
        p: { xs: 0.5, sm: 2 },
        overflow: "auto",
      }}
    >
      {/* Loading Overlay */}
      {loadingStep ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: 2,
            zIndex: 1500,
            backdropFilter: "blur(2px)",
            height: "100%",
          }}
        >
          {/* loading overlay */}
          <Loading message={loadingStep} fullScreen={false} />
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: "white",
            borderRadius: { xs: 1, sm: 4 },
            m: { xs: 0.25, sm: 1 },
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            width: { xs: "100%", sm: "600px" },
            maxHeight: { xs: "100vh", sm: "100vh" },
            overflow: "auto",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, maxWidth: "450px" }}
            >
              Start your unlimited access with All Features Included just ₹
              {price} for the 1st month
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "0.9rem",
                fontWeight: 500,
                fontStyle: "italic",
                color: "primary.main",
              }}
            >
              Track Income. Control Expenses. Stay Compliant.
            </Typography>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1 }}
              >
                Key Features:
              </Typography>

              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {featuresToShow.map((feature, index) => (
                  <Typography
                    key={index}
                    component="li"
                    variant="body2"
                    sx={{ fontSize: "0.8rem", mb: 0.5 }}
                  >
                    {feature}
                  </Typography>
                ))}
              </Box>

              {keyFeatures.length > INITIAL_FEATURES_COUNT && (
                <Button
                  variant="text"
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                  sx={{
                    p: 0,
                    mt: 1,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "primary.main",
                    fontWeight: 500,
                    minHeight: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {showAllFeatures ? `Show Less` : `Show More`}
                </Button>
              )}
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                fontStyle: "italic",
              }}
            >
              From 2nd month : Simple per-vehicle pricing based on your fleet
              size
            </Typography>

            {/* Payment Gateway Status */}
            {!razorpayLoaded && !razorpayLoadError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                >
                  Loading secure payment gateway...
                </Typography>
              </Box>
            )}

            {razorpayLoadError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  p: 1,
                  backgroundColor: "#ffebee",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "error.main", fontSize: "0.8rem" }}
                >
                  Payment gateway failed to load.
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={retryRazorpaySDK}
                  sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                >
                  Retry
                </Button>
              </Box>
            )}

            {razorpayLoaded && !razorpayLoadError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  backgroundColor: "#e8f5e8",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "success.main", fontSize: "0.8rem" }}
                >
                  ✅ Secure payment gateway ready
                </Typography>
              </Box>
            )}

            <Divider sx={{ borderColor: "#e2e8f0" }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  handleLogout(queryClient);
                  // close the dialog
                  onClose();
                }}
                {...smallButtonProps}
              >
                Logout
              </Button>
              {loadingStep ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  <Typography variant="body2">{loadingStep}</Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  {...smallButtonProps}
                  sx={{
                    minWidth: "150px",
                    opacity:
                      !razorpayLoaded ||
                      razorpayLoadError ||
                      isSubmitting ||
                      isProcessing
                        ? 0.6
                        : 1,
                  }}
                  onClick={createPaymentOrder}
                  disabled={
                    !razorpayLoaded ||
                    razorpayLoadError ||
                    isSubmitting ||
                    isProcessing
                  }
                >
                  <Typography variant="body2">
                    {!razorpayLoaded && !razorpayLoadError
                      ? "Loading..."
                      : razorpayLoadError
                      ? "Refresh Page"
                      : `Pay ₹${price}`}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ActivationDialog;
